import React, { useState } from 'react';

const MARKETPLACE_BASE = 'https://www.kromarketplace.com';

const partnerTiers = [
  { tier: 'Distribution Supplier', icon: 'bi-shop', color: '#e879f9', desc: 'Verified distribution suppliers listed on KRO Marketplace > Browse Products > Distribution Channel Partners. Ethics-audited, ESG-verified, and actively contributing to positive outcomes across the trust-based capital ecosystem.' },
  { tier: 'Institutional', icon: 'bi-bank2', color: '#c9a84c', desc: 'Regulated institutional participants with LEI-gated settlement access. Direct Canton Network participant nodes.' },
  { tier: 'Platform', icon: 'bi-window-stack', color: '#818cf8', desc: 'Technology and infrastructure partners providing exchange, custody, and compliance services across the KRO ecosystem.' },
  { tier: 'Supplier', icon: 'bi-truck', color: '#10b981', desc: 'Real-world asset originators, commodity suppliers, and service providers connected via the KRO supply chain.' },
  { tier: 'Community', icon: 'bi-people-fill', color: '#f472b6', desc: 'Retail and community-level distribution — activates in the next growth phase for full B2ALL participation.' },
];

const distributionPartners = [
  // ── Distribution Suppliers (from KRO Marketplace > Browse Products > Distribution Channel Partners) ──
  { name: '1687 Productions', role: 'Creative & Media Distribution', tier: 'Distribution Supplier', status: 'Active', desc: 'Full-service creative production and media distribution partner. Delivers brand storytelling, video production, and digital content strategy across the KRO trust ecosystem.', link: `${MARKETPLACE_BASE}/partner/1687-productions`, icon: 'bi-camera-reels-fill', color: '#e879f9', metrics: { type: 'Media' } },
  { name: 'AI for Job Security Foundation', role: 'Workforce & AI Ethics', tier: 'Distribution Supplier', status: 'Active', desc: 'Nonprofit foundation ensuring AI-driven economic transitions protect worker dignity and job security. Distributes workforce development programs and ethical AI toolkits through the KRO ecosystem.', link: `${MARKETPLACE_BASE}/partner/ai-job-security`, icon: 'bi-robot', color: '#38bdf8', metrics: { type: 'Workforce' } },
  { name: 'So Fraiche', role: 'Sustainable Consumer Goods', tier: 'Distribution Supplier', status: 'Active', desc: 'Eco-conscious consumer goods brand specializing in sustainable personal care and lifestyle products. Ethically sourced, cruelty-free, and zero-waste packaging.', link: `${MARKETPLACE_BASE}/partner/sofraiche-media`, icon: 'bi-droplet-fill', color: '#10b981', metrics: { type: 'Consumer Goods' } },
  { name: 'African Supplier Network', role: 'Pan-African Supply Chain', tier: 'Distribution Supplier', status: 'Active', desc: 'Pan-African trade network connecting commodity originators, manufacturers, and service providers. Distributes fair-trade coffee, cocoa, minerals, and agricultural products.', link: `${MARKETPLACE_BASE}/browse-products`, icon: 'bi-globe-africa', color: '#f59e0b', metrics: { type: 'Trade Network' } },
  { name: 'Sexi Cycle', role: 'Circular Economy & Upcycling', tier: 'Distribution Supplier', status: 'Active', desc: 'Circular economy innovator transforming waste streams into premium upcycled products. Distributes sustainable fashion, accessories, and materials through ethical retail channels.', link: `${MARKETPLACE_BASE}/partner/sexicycle-productions`, icon: 'bi-recycle', color: '#f472b6', metrics: { type: 'Circular Economy' } },

  // ── Infrastructure Partners ──
  { name: 'Canton Network', role: 'Settlement Layer', tier: 'Institutional', status: 'Active', desc: 'Global Synchronizer and Daml ledger — provides deterministic DvP settlement infrastructure for all KRO intraday repo draws.', link: 'https://www.canton.network', icon: 'bi-diagram-3-fill', color: '#38bdf8', metrics: { type: 'Settlement' } },
  { name: 'BitGo Enterprise', role: 'MPC Custody', tier: 'Institutional', status: 'Active', desc: 'Enterprise multi-party computation (MPC) custody — HASH wallet for Provenance markers, ETH wallet for USDT escrow.', link: 'https://www.bitgo.com', icon: 'bi-shield-lock-fill', color: '#f59e0b', metrics: { type: 'Custody' } },
  { name: 'Provenance Blockchain', role: 'Asset Registry', tier: 'Platform', status: 'Active', desc: 'On-chain asset registry (pio-mainnet-1) — hosts all KRO marker denoms with DTIF identifiers and ZoneScan verification.', link: 'https://provenance.io', icon: 'bi-box-seam-fill', color: '#a78bfa', metrics: { type: 'Registry' } },
  { name: 'Mercury', role: 'Banking Rail', tier: 'Institutional', status: 'Active', desc: 'API-driven banking and correspondent rail for ISO 20022 payment messaging (pacs.008/009/028/camt.054) and FedWire RTGS integration.', link: 'https://mercury.com', icon: 'bi-bank', color: '#10b981', metrics: { type: 'Payments' } },
  { name: 'Column N.A.', role: 'Correspondent Bank', tier: 'Institutional', status: 'Active', desc: 'Nationally chartered bank providing FedWire RTGS access. Completes the USD settlement leg for institutional draws.', link: 'https://column.com', icon: 'bi-building', color: '#64748b', metrics: { type: 'Banking' } },
  { name: 'KRO Marketplace', role: 'Exchange & Distribution', tier: 'Platform', status: 'Active', desc: 'Public-facing marketplace for product discovery, TTD portal, and KRO Exchange. Connects all distribution tiers.', link: MARKETPLACE_BASE, icon: 'bi-shop-window', color: '#c9a84c', metrics: { type: 'Exchange' } },
  { name: 'KRO Trust Index', role: 'Scoring & Verification', tier: 'Platform', status: 'Live', desc: 'Composite trust score engine derived from settlement finality, governance integrity, and ESG impact — published on-chain via Provenance.', link: `${MARKETPLACE_BASE}/ttd-portal?page=portfolio`, icon: 'bi-award-fill', color: '#f59e0b', metrics: { type: 'Index' } },
  { name: 'Supplier Network', role: 'Commodity Originators', tier: 'Supplier', status: 'Active', desc: 'Fair-trade commodity suppliers onboarded via the Supplier Ecosystem — ethics-verified and ESG-audited.', link: `${MARKETPLACE_BASE}/fair-trade-coffee-beans`, icon: 'bi-tree-fill', color: '#10b981', metrics: { type: 'Supply Chain' } },
  { name: 'Community Rail (Phase 2)', role: 'Consumer Payment Rails', tier: 'Community', status: 'Planned', desc: 'Upcoming consumer-facing payment rails for Suppliers, Enterprises, and everyday consumers — enabling full B2ALL participation across the trust-based capital ecosystem.', link: null, icon: 'bi-people-fill', color: '#f472b6', metrics: { type: 'Payments' } },
];

const supplierOutcomes = distributionPartners.filter(p => p.tier === 'Distribution Supplier');

const channelFlow = [
  { step: 1, label: 'Asset Origination', desc: 'Real-world assets listed on KRO Marketplace by verified suppliers', icon: 'bi-box-arrow-in-right', color: '#c9a84c' },
  { step: 2, label: 'Tokenization & Registry', desc: 'Markers minted on Provenance (pio-mainnet-1) with DTIF identifiers', icon: 'bi-tag-fill', color: '#a78bfa' },
  { step: 3, label: 'Custody & Escrow', desc: 'BitGo MPC custody — HASH wallet (markers) + ETH wallet (USDT escrow)', icon: 'bi-shield-lock-fill', color: '#f59e0b' },
  { step: 4, label: 'Settlement & DvP', desc: 'Canton Network atomic DvP — deterministic, privacy-preserving settlement', icon: 'bi-lightning-charge-fill', color: '#38bdf8' },
  { step: 5, label: 'Payment Rail', desc: 'Mercury → Column N.A. → FedWire RTGS — ISO 20022 compliant', icon: 'bi-bank', color: '#10b981' },
  { step: 6, label: 'Distribution', desc: 'Products visible on KRO Exchange (institutional) → B2ALL (Phase 2)', icon: 'bi-shop-window', color: '#818cf8' },
];

const DistributionPartners = () => {
  const [activeView, setActiveView] = useState('partners');
  const [filterTier, setFilterTier] = useState('All');

  const filtered = filterTier === 'All' ? distributionPartners : distributionPartners.filter(p => p.tier === filterTier);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' }}>
    <div className="container-fluid py-4" style={{ color: '#e2e8f0' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(201,168,76,0.15)' }}>
        <div>
          <h2 className="fw-bold mb-1" style={{ color: '#c9a84c' }}>
            <i className="bi bi-diagram-3-fill me-2"></i>Distribution Channel Partners
          </h2>
          <p className="mb-0" style={{ color: '#94a3b8', fontSize: '0.88rem' }}>
            Integrated with <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c' }}>KRO Marketplace &gt; Browse Products</a> · Settlement infra &amp; admin governance — compliant by design
          </p>
        </div>
        <div className="d-flex gap-2">
          <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" className="btn btn-sm" style={{ background: '#c9a84c', color: '#0f172a', fontWeight: 700 }}>
            <i className="bi bi-shop me-1"></i>Browse Products
          </a>
          <a href={`${MARKETPLACE_BASE}/ttd-portal?page=portfolio`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light">
            <i className="bi bi-window-fullscreen me-1"></i>KRO Exchange
          </a>
        </div>
      </div>

      {/* Tabs */}
      <ul className="nav nav-pills mb-4">
        {[
          { id: 'partners', label: 'Partners', icon: 'bi-people-fill' },
          { id: 'tiers', label: 'Distribution Tiers', icon: 'bi-layers-fill' },
          { id: 'flow', label: 'Channel Flow', icon: 'bi-arrow-right-circle-fill' },
        ].map(v => (
          <li key={v.id} className="nav-item">
            <button className={`nav-link ${activeView === v.id ? 'active' : ''}`} onClick={() => setActiveView(v.id)}>
              <i className={`bi ${v.icon} me-1`}></i>{v.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ════ PARTNERS ════ */}
      {activeView === 'partners' && (
        <>
          {/* Summary strip */}
          <div className="row g-3 mb-4">
            {[
              { label: 'Distribution Suppliers', val: supplierOutcomes.length, icon: 'bi-shop', color: '#e879f9' },
              { label: 'Active Partners', val: distributionPartners.filter(p => p.status === 'Active' || p.status === 'Live').length, icon: 'bi-check-circle-fill', color: '#10b981' },
              { label: 'Total Partners', val: distributionPartners.length, icon: 'bi-diagram-3-fill', color: '#c9a84c' },
              { label: 'Planned', val: distributionPartners.filter(p => p.status === 'Planned').length, icon: 'bi-clock-fill', color: '#f472b6' },
            ].map((s, i) => (
              <div key={i} className="col-6 col-lg-3">
                <div className="card border-0 shadow-sm text-center" style={{ background: '#0f172a' }}>
                  <div className="card-body py-3">
                    <i className={`bi ${s.icon}`} style={{ fontSize: '1.3rem', color: s.color }}></i>
                    <div className="fw-bold fs-4" style={{ color: s.color }}>{s.val}</div>
                    <small style={{ color: '#64748b' }}>{s.label}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tier filter */}
          <div className="d-flex gap-2 mb-3 flex-wrap">
            {['All', ...partnerTiers.map(t => t.tier)].map(t => (
              <button key={t} className={`btn btn-sm ${filterTier === t ? 'btn-light' : 'btn-outline-secondary'}`} onClick={() => setFilterTier(t)} style={{ fontSize: '0.78rem' }}>
                {t}
              </button>
            ))}
          </div>

          {/* Partner cards */}
          <div className="row g-3">
            {filtered.map((p, i) => (
              <div key={i} className="col-md-6 col-xl-4">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${p.color}22` }}>
                  <div className="card-body">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${p.icon}`} style={{ fontSize: '1.5rem', color: p.color }}></i>
                        <div>
                          <strong style={{ fontSize: '0.95rem' }}>{p.name}</strong>
                          <small className="d-block" style={{ color: '#64748b' }}>{p.role}</small>
                        </div>
                      </div>
                      <span className={`badge ${p.status === 'Active' || p.status === 'Live' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }}>{p.status}</span>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.5 }}>{p.desc}</p>
                    <div className="d-flex gap-3 mb-3" style={{ fontSize: '0.72rem' }}>
                      <div><small style={{ color: '#475569' }}>Type</small><div className="fw-bold" style={{ color: '#e2e8f0' }}>{p.metrics.type}</div></div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge" style={{ background: `${p.color}22`, color: p.color, fontSize: '0.6rem' }}>{p.tier}</span>
                      {p.link && (
                        <a href={p.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm flex-grow-1" style={{ fontSize: '0.72rem', background: `${p.color}12`, color: p.color, border: `1px solid ${p.color}33` }}>
                          <i className="bi bi-box-arrow-up-right me-1"></i>Visit
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ════ TIERS ════ */}
      {activeView === 'tiers' && (
        <div className="row g-3">
          {partnerTiers.map((t, i) => {
            const partners = distributionPartners.filter(p => p.tier === t.tier);
            return (
              <div key={i} className="col-lg-6">
                <div className="card border-0 shadow-sm h-100" style={{ background: '#0f172a', border: `1px solid ${t.color}22` }}>
                  <div className="card-header border-0 d-flex align-items-center justify-content-between" style={{ background: `${t.color}08` }}>
                    <h5 className="mb-0 fw-bold" style={{ color: t.color }}>
                      <i className={`bi ${t.icon} me-2`}></i>{t.tier}
                    </h5>
                    <span className="badge" style={{ background: `${t.color}22`, color: t.color }}>{partners.length} partner{partners.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="card-body">
                    <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5 }}>{t.desc}</p>
                    <hr style={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    {partners.length > 0 ? partners.map((p, pi) => (
                      <div key={pi} className="d-flex align-items-center gap-2 mb-2">
                        <i className={`bi ${p.icon}`} style={{ color: p.color, fontSize: '1rem' }}></i>
                        <div className="flex-grow-1">
                          <strong style={{ fontSize: '0.82rem' }}>{p.name}</strong>
                          <small className="d-block" style={{ color: '#64748b', fontSize: '0.7rem' }}>{p.role}</small>
                        </div>
                        <span className={`badge ${p.status === 'Active' || p.status === 'Live' ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '0.55rem' }}>{p.status}</span>
                      </div>
                    )) : (
                      <small style={{ color: '#475569' }}>No active partners — activates in Phase 2</small>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ════ CHANNEL FLOW ════ */}
      {activeView === 'flow' && (
        <div className="card border-0 shadow-sm" style={{ background: '#0f172a' }}>
          <div className="card-header border-0" style={{ background: 'linear-gradient(135deg, #1e1b4b, #0f172a)' }}>
            <h5 className="mb-0 fw-bold text-white">
              <i className="bi bi-arrow-right-circle-fill me-2 text-info"></i>Distribution Channel Flow
            </h5>
            <small style={{ color: '#94a3b8' }}>End-to-end flow from asset origination to distribution across all partner tiers</small>
          </div>
          <div className="card-body">
            {channelFlow.map((s, i) => (
              <div key={i} className="d-flex align-items-start gap-3 mb-4">
                <div className="flex-shrink-0 text-center" style={{ width: 48 }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 40, height: 40, background: `${s.color}22`, border: `2px solid ${s.color}` }}>
                    <i className={`bi ${s.icon}`} style={{ color: s.color }}></i>
                  </div>
                  {i < channelFlow.length - 1 && (
                    <div style={{ width: 2, height: 28, background: 'rgba(255,255,255,0.08)', margin: '4px auto' }}></div>
                  )}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="badge" style={{ background: `${s.color}22`, color: s.color, fontSize: '0.6rem' }}>STEP {s.step}</span>
                    <strong style={{ fontSize: '0.95rem' }}>{s.label}</strong>
                  </div>
                  <p className="mb-0" style={{ fontSize: '0.82rem', color: '#94a3b8' }}>{s.desc}</p>
                </div>
              </div>
            ))}
            <div className="mt-2 p-3 rounded text-center" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
              <i className="bi bi-shop-window me-2" style={{ color: '#c9a84c' }}></i>
              <strong style={{ color: '#c9a84c' }}>All channels converge on </strong>
              <a href={`${MARKETPLACE_BASE}/browse-products`} target="_blank" rel="noopener noreferrer" style={{ color: '#c9a84c', fontWeight: 700 }}>
                KRO Marketplace &gt; Browse Products
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-4 p-3 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.72rem', color: '#475569' }}>
        <i className="bi bi-info-circle me-1"></i>
        KRO Brokers, SPC operates as settlement infrastructure and admin governance — compliant by design.
        KRO is not a bank, SEC-regulated broker, clearing agent, or counterparty.
        Distribution channel partners are independent third parties. Listing does not imply endorsement, partnership certification, or regulatory coverage.
        Product availability on <a href={MARKETPLACE_BASE} target="_blank" rel="noopener noreferrer" style={{ color: '#64748b' }}>kromarketplace.com</a>.
      </div>
    </div>
    </div>
  );
};

export default DistributionPartners;
